import { useTranslation } from '@platform/i18n';
import { isPriceBelowCost, type ProductFormIssue } from '../product-form.ts';

export const EDITOR_SECTIONS = [
  'essentials',
  'media',
  'pricing',
  'variants',
  'stock',
  'shipping',
  'more',
] as const;

export type EditorSection = (typeof EDITOR_SECTIONS)[number];

export const sectionElementId = (section: EditorSection): string => `product-editor-${section}`;

type FieldPlacement = { readonly section: EditorSection; readonly label: string };

const FIELD_PLACEMENTS: Readonly<Record<string, FieldPlacement>> = {
  options: { section: 'variants', label: 'productEditor.variants' },
  media: { section: 'media', label: 'productEditor.media' },
  translations: { section: 'essentials', label: 'catalog.name' },
  reference: { section: 'essentials', label: 'catalog.reference' },
  price: { section: 'pricing', label: 'productEditor.sellingPrice' },
  regularPrice: { section: 'pricing', label: 'productEditor.regularPrice' },
  cost: { section: 'pricing', label: 'productEditor.cost' },
  variants: { section: 'variants', label: 'productEditor.sellingPrice' },
  stockTotals: { section: 'stock', label: 'productEditor.stockTotal' },
  stockSplits: { section: 'stock', label: 'productEditor.stockSplitQuantity' },
  thresholds: { section: 'stock', label: 'productEditor.stockThreshold' },
  weightKg: { section: 'shipping', label: 'productEditor.weight' },
  lengthCm: { section: 'shipping', label: 'productEditor.lengthCm' },
  widthCm: { section: 'shipping', label: 'productEditor.widthCm' },
  heightCm: { section: 'shipping', label: 'productEditor.heightCm' },
  warrantyMonths: { section: 'more', label: 'productEditor.warranty' },
};

const VARIANT_LIST_LABEL = 'productEditor.variants';

const VARIANT_COST_LABEL = 'productEditor.cost';

const placementOf = (issue: ProductFormIssue): FieldPlacement | undefined =>
  FIELD_PLACEMENTS[String(issue.path[0])];

export const sectionOfIssue = (issue: ProductFormIssue): EditorSection =>
  placementOf(issue)?.section ?? 'essentials';

const isVariantList = (issue: ProductFormIssue): boolean =>
  issue.path.length === 1 && issue.path[0] === 'variants';

const isVariantCost = (issue: ProductFormIssue): boolean =>
  issue.path[0] === 'variants' && issue.path[2] === 'cost';

export const fieldLabelOfIssue = (issue: ProductFormIssue): string => {
  if (isVariantList(issue)) return VARIANT_LIST_LABEL;
  if (isVariantCost(issue)) return VARIANT_COST_LABEL;
  return placementOf(issue)?.label ?? 'productEditor.essentials';
};

export const useFieldError = () => {
  const { t } = useTranslation();
  return (error: { readonly message?: string | undefined } | undefined): string | undefined =>
    error?.message ? t(`productEditor.errors.${error.message}`) : undefined;
};

export const usePriceError = () => {
  const { t } = useTranslation();
  const errorText = useFieldError();
  return (
    error: { readonly message?: string | undefined } | undefined,
    { price, cost }: { readonly price: string; readonly cost: string },
  ): string | undefined =>
    errorText(error) ??
    (isPriceBelowCost(price, cost) ? t('productEditor.errors.priceBelowCost') : undefined);
};

export const variantField = <K extends 'sku' | 'barcode' | 'price' | 'cost' | 'enabled'>(
  index: number,
  key: K,
) => `variants.${String(index)}.${key}` as `variants.${number}.${K}`;
